/**
 * DocumentsWidget — Dashboard widget (Iter28).
 *
 * Shows at-a-glance document insights for the logged-in user:
 *  - Per-scope counts (Schools / Women / General) — only scopes the user can see
 *  - Top 5 expiring documents (next 30 days) with urgency badges
 *  - Total documents + recent uploads (last 7 days)
 *  - "Open Documents" CTA → /documents
 *
 * Auto-hides if the user has no accessible scopes.
 *
 * Data: /api/documents/summary (already returns by_scope) + /api/documents/expiring?days=30
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Skeleton } from './ui/skeleton';
import { FolderOpen, FileText, AlertTriangle, ArrowRight, Calendar, Upload } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SCOPE_LABEL = { schools: 'Schools', women: 'Women Devt', general: 'General' };
const SCOPE_TONE = {
  schools: 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300 border-blue-200',
  women: 'bg-pink-50 text-pink-700 dark:bg-pink-900/20 dark:text-pink-300 border-pink-200',
  general: 'bg-slate-50 text-slate-700 dark:bg-slate-800/60 dark:text-slate-200 border-slate-200',
};
const URGENCY_TONE = {
  critical: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  warning: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  info: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
};

const DocumentsWidget = () => {
  const { allowedDocumentScopes } = useAuth();
  const accessible = allowedDocumentScopes ? allowedDocumentScopes('view') : [];
  const [summary, setSummary] = useState(null);
  const [expiring, setExpiring] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (accessible.length === 0) {
      setLoading(false);
      return;
    }
    let cancel = false;
    Promise.all([
      axios.get(`${API_URL}/api/documents/summary`),
      axios.get(`${API_URL}/api/documents/expiring`, { params: { days: 30 } }),
    ])
      .then(([s, e]) => {
        if (cancel) return;
        setSummary(s.data || null);
        setExpiring((e.data || []).slice(0, 5));
      })
      .catch(() => {})
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [accessible.length]);

  if (accessible.length === 0) return null;

  return (
    <Card className="card-hover" data-testid="documents-widget">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="font-heading flex items-center gap-2">
              <FolderOpen className="h-5 w-5 text-primary" /> Documents & Forms
            </CardTitle>
            <CardDescription>
              {summary
                ? `${summary.total} active · ${summary.recent_uploads} uploaded this week`
                : 'Recent activity at a glance'}
            </CardDescription>
          </div>
          <Button asChild variant="ghost" size="sm" data-testid="documents-widget-open">
            <Link to="/documents">Open <ArrowRight className="h-3.5 w-3.5 ml-1" /></Link>
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Per-scope counts */}
        {loading ? (
          <div className="grid grid-cols-3 gap-2">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-16" />)}
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2" data-testid="documents-widget-scopes">
            {accessible.map((sk) => {
              const count = summary?.by_scope?.[sk] ?? 0;
              return (
                <Link
                  key={sk}
                  to={`/documents?scope=${sk}`}
                  className={`rounded-md border p-3 text-center transition-colors hover:opacity-90 ${SCOPE_TONE[sk]}`}
                  data-testid={`documents-widget-scope-${sk}`}
                >
                  <div className="text-2xl font-heading font-bold">{count}</div>
                  <div className="text-[11px] uppercase tracking-wide font-medium">
                    {SCOPE_LABEL[sk] || sk}
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {/* Expiring soon */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-500" /> Expiring next 30 days
            </span>
            {summary?.expiring_soon > 0 && (
              <Badge variant="secondary" data-testid="documents-widget-expiring-count">
                {summary.expiring_soon}
              </Badge>
            )}
          </div>
          {loading ? (
            <Skeleton className="h-20" />
          ) : expiring.length === 0 ? (
            <div className="text-xs text-muted-foreground rounded-md border border-dashed p-3 text-center">
              Nothing expiring soon — you're all clear.
            </div>
          ) : (
            <ul className="space-y-1.5">
              {expiring.map((d) => (
                <li
                  key={d.id}
                  className="flex items-center justify-between text-sm rounded-md border bg-card/40 px-2.5 py-1.5"
                  data-testid={`documents-widget-expiring-${d.id}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="truncate">{d.name}</span>
                    <Badge variant="outline" className={`text-[10px] ${SCOPE_TONE[d.scope] || ''}`}>
                      {SCOPE_LABEL[d.scope] || d.scope}
                    </Badge>
                  </div>
                  <Badge className={`text-[10px] ${URGENCY_TONE[d.urgency] || URGENCY_TONE.info}`}>
                    <Calendar className="h-2.5 w-2.5 mr-1" />
                    {d.days_to_expiry !== null
                      ? `${d.days_to_expiry}d`
                      : 'soon'}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Quick actions */}
        {summary && (
          <div className="flex items-center gap-2 pt-1 border-t">
            <Button asChild variant="outline" size="sm" className="flex-1" data-testid="documents-widget-upload">
              <Link to="/documents"><Upload className="h-3.5 w-3.5 mr-1.5" /> Upload</Link>
            </Button>
            <Button asChild variant="outline" size="sm" className="flex-1" data-testid="documents-widget-browse">
              <Link to="/documents">Browse all</Link>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DocumentsWidget;
